/* eslint-disable max-lines-per-function */
import mime from 'mime';
import saveAs from 'file-saver';

import { DefaultApi } from '../autogenerated/pokApiClient';
import { FilterYearReportData } from '../../../pok/components/Reports/types/FilterYearTypes';

export interface ReportContextInterface {
  reportEstimateItemsRawData: (
    filename: string,
    filter: FilterYearReportData,
  ) => Promise<void>;

  reportContractsMonitoring: (
    filename: string,
    filter: FilterYearReportData,
  ) => Promise<void>;

  reportProjectByMonthRawData: (
    filename: string,
    filter: FilterYearReportData,
  ) => Promise<void>;

  reportFVSByFilterRawData: (
    filename: string,
    filter: FilterYearReportData,
    withPositions: boolean,
  ) => Promise<void>;

  reportFVZByFilterRawData: (
    filename: string,
    filter: FilterYearReportData,
  ) => Promise<void>;

  reportFVZControllingByFilterRawData: (
    filename: string,
    filter: FilterYearReportData,
  ) => Promise<void>;

  reportFVSControllingByFilterRawData: (
    filename: string,
    filter: FilterYearReportData,
  ) => Promise<void>;

  getReportForDocumentControl: (
    filename: string,
    budgetId?: string,
    projectId?: string,
  ) => Promise<void>;

  reportExcelForSalesInvoicing: (
    filename: string,
    invoiced?: boolean,
    month?: Date,
    companyId?: string,
    withAllCompany?: boolean,
  ) => Promise<void>;

  reportExcelForPurchaseWithTeams: (filename: string) => Promise<void>;
  reportExcelForPurchaseDeficiencies: (
    filename: string,
    filter: FilterYearReportData,
  ) => Promise<void>;

  reportProjectsReadyForInvoice: (
    filename: string,
    filter: FilterYearReportData,
  ) => Promise<void>;

  reportRecommendedPublishersRawData: (filename: string) => Promise<void>;

  reportClientsContacts: (filename: string) => Promise<void>;

  reportSurveyResults: (
    filename: string,
    surveyId: string,
    year?: number,
    companyId?: string,
  ) => Promise<void>;

  reportSurveyResultsWithRemarks: (
    filename: string,
    surveyId: string,
    year?: number,
    companyId?: string,
  ) => Promise<void>;

  reportFVSEstimateItemsRawData: (
    filename: string,
    filter: FilterYearReportData,
  ) => Promise<void>;
}

export const ReportContext = (api: DefaultApi) => {
  return {
    reportEstimateItemsRawData: async (
      filename: string,
      filter: FilterYearReportData,
    ) => {
      return await api
        .reportControllerReportEstimateItemsRawData({ ...filter })
        .then(async fileContent => {
          const blob = new Blob([Buffer.from(fileContent as ArrayBuffer)], {
            type: mime.getType(filename) || undefined,
          });
          await saveAs(blob, filename);
        });
    },

    reportContractsMonitoring: async (
      filename: string,
      filter: FilterYearReportData,
    ) => {
      return await api
        .reportControllerReportExcelForContractsMonitoring({ ...filter })
        .then(async fileContent => {
          const blob = new Blob([Buffer.from(fileContent as ArrayBuffer)], {
            type: mime.getType(filename) || undefined,
          });
          await saveAs(blob, filename);
        });
    },

    reportProjectByMonthRawData: async (
      filename: string,
      filter: FilterYearReportData,
    ) => {
      return await api
        .reportControllerReportProjectByMonthRawData({ ...filter })
        .then(async fileContent => {
          const blob = new Blob([Buffer.from(fileContent as ArrayBuffer)], {
            type: mime.getType(filename) || undefined,
          });
          await saveAs(blob, filename);
        });
    },

    reportFVSByFilterRawData: async (
      filename: string,
      filter: FilterYearReportData,
      withPositions: boolean,
    ) => {
      return await api
        .reportControllerReportFVSByFilterRawData({
          ...filter,
          withPositions,
        })
        .then(async fileContent => {
          const blob = new Blob([Buffer.from(fileContent as ArrayBuffer)], {
            type: mime.getType(filename) || undefined,
          });
          await saveAs(blob, filename);
        });
    },

    reportFVZByFilterRawData: async (
      filename: string,
      filter: FilterYearReportData,
    ) => {
      return await api
        .reportControllerReportFVZByFilterRawData({ ...filter })
        .then(async fileContent => {
          const blob = new Blob([Buffer.from(fileContent as ArrayBuffer)], {
            type: mime.getType(filename) || undefined,
          });
          await saveAs(blob, filename);
        });
    },

    reportFVZControllingByFilterRawData: async (
      filename: string,
      filter: FilterYearReportData,
    ) => {
      return await api
        .reportControllerReportFVZControllingByFilterRawData({ ...filter })
        .then(async fileContent => {
          const blob = new Blob([Buffer.from(fileContent as ArrayBuffer)], {
            type: mime.getType(filename) || undefined,
          });
          await saveAs(blob, filename);
        });
    },

    reportFVSControllingByFilterRawData: async (
      filename: string,
      filter: FilterYearReportData,
    ) => {
      return await api
        .reportControllerReportFVSControllingByFilterRawData({ ...filter })
        .then(async fileContent => {
          const blob = new Blob([Buffer.from(fileContent as ArrayBuffer)], {
            type: mime.getType(filename) || undefined,
          });
          await saveAs(blob, filename);
        });
    },

    getReportForDocumentControl: async (
      filename: string,
      budgetId?: string,
      projectId?: string,
    ) => {
      return await api
        .reportControllerReportForDocumentControl(budgetId, projectId)
        .then(async fileContent => {
          const blob = new Blob([Buffer.from(fileContent as ArrayBuffer)], {
            type: mime.getType(filename) || undefined,
          });
          await saveAs(blob, filename);
        });
    },

    reportExcelForSalesInvoicing: async (
      filename: string,
      invoiced?: boolean,
      month?: Date,
      companyId?: string,
      withAllCompany?: boolean,
    ) => {
      return await api
        .reportControllerReportExcelForSalesInvoicing(
          invoiced,
          month,
          companyId,
          withAllCompany,
        )
        .then(async fileContent => {
          const blob = new Blob([Buffer.from(fileContent as ArrayBuffer)], {
            type: mime.getType(filename) || undefined,
          });
          await saveAs(blob, filename);
        });
    },

    reportExcelForPurchaseWithTeams: async (filename: string) => {
      return await api
        .reportControllerReportExcelForPurchaseWithTeams()
        .then(async fileContent => {
          const blob = new Blob([Buffer.from(fileContent as ArrayBuffer)], {
            type: mime.getType(filename) || undefined,
          });
          await saveAs(blob, filename);
        });
    },
    reportExcelForPurchaseDeficiencies: async (
      filename: string,
      filter: FilterYearReportData,
    ) => {
      return await api
        .reportControllerReportExcelForPurchaseDeficiencies({ ...filter })
        .then(async fileContent => {
          const blob = new Blob([Buffer.from(fileContent as ArrayBuffer)], {
            type: mime.getType(filename) || undefined,
          });
          await saveAs(blob, filename);
        });
    },

    reportProjectsReadyForInvoice: async (
      filename: string,
      filter: FilterYearReportData,
    ) => {
      return await api
        .reportControllerReportProjectsReadyForInvoice({ ...filter })
        .then(async fileContent => {
          const blob = new Blob([Buffer.from(fileContent as ArrayBuffer)], {
            type: mime.getType(filename) || undefined,
          });
          await saveAs(blob, filename);
        });
    },

    reportRecommendedPublishersRawData: async (filename: string) => {
      return await api
        .reportControllerReportExcelForRecommendedPublishers()
        .then(async fileContent => {
          const blob = new Blob([Buffer.from(fileContent as ArrayBuffer)], {
            type: mime.getType(filename) || undefined,
          });
          await saveAs(blob, filename);
        });
    },

    reportClientsContacts: async (filename: string) => {
      return await api
        .reportControllerReportExcelForClientsContacts()
        .then(async fileContent => {
          const blob = new Blob([Buffer.from(fileContent as ArrayBuffer)], {
            type: mime.getType(filename) || undefined,
          });
          await saveAs(blob, filename);
        });
    },

    reportSurveyResults: async (
      filename: string,
      surveyId: string,
      year?: number,
      companyId?: string,
    ) => {
      return await api
        .reportControllerReportExcelForSurveyResults({
          surveyId,
          year,
          companyId,
        })
        .then(async fileContent => {
          const blob = new Blob([Buffer.from(fileContent as ArrayBuffer)], {
            type: mime.getType(filename) || undefined,
          });
          await saveAs(blob, filename);
        });
    },

    reportSurveyResultsWithRemarks: async (
      filename: string,
      surveyId: string,
      year?: number,
      companyId?: string,
    ) => {
      return await api
        .reportControllerReportExcelForSurveyResultsWithRemarks({
          surveyId,
          year,
          companyId,
        })
        .then(async fileContent => {
          const blob = new Blob([Buffer.from(fileContent as ArrayBuffer)], {
            type: mime.getType(filename) || undefined,
          });
          await saveAs(blob, filename);
        });
    },

    reportFVSEstimateItemsRawData: async (
      filename: string,
      filter: FilterYearReportData,
    ) => {
      return await api
        .reportControllerReportExcelForFVSEstimateItems({ ...filter })
        .then(async fileContent => {
          const blob = new Blob([Buffer.from(fileContent as ArrayBuffer)], {
            type: mime.getType(filename) || undefined,
          });
          await saveAs(blob, filename);
        });
    },
  };
};
